import { Stack, Typography } from "@mui/material";
import { useCallback, useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router";
import { Link, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormTextField, PrimaryButton } from "../../../../components/form";
import { APP_COLORS } from "../../../../enums";
import { userSignup } from "../../../../api/auth";
import { getUserProfile } from "../../../../api/profile";
import { useMutationWithDispatch } from "../../../../hooks/useMutationWithDispatch";
import { ROUTE_PATHS } from "../../../../routes/paths";
import {
  ambassadorSignupSchema,
  AmbassadorSignupFormData,
  PAGE_CONFIG,
} from "./config";
import {
  handleAmbassadorSignupSuccess,
  getAmbassadorSignupPayload,
} from "./utils";
import { AmbassadorSignupResponse, OutletContext } from "./types";
import ExistingAmbassadorSignup from "./signup-existing-profile";

function AmbassadorSignup() {
  const { setPageConfig } = useOutletContext<OutletContext>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<AmbassadorSignupFormData>({
    resolver: zodResolver(ambassadorSignupSchema),
    defaultValues: {
      email: "",
      firstName: "",
      lastName: "",
      password: "",
      confirmPassword: "",
    },
  });

  const { mutateAsync: submitSignup, isPending: submitting } =
    useMutationWithDispatch<
      AmbassadorSignupResponse,
      {
        email: string;
        firstName: string;
        lastName: string;
        password: string;
        role: string;
      }
    >((payload) => userSignup(payload));

  const { mutateAsync: fetchUserProfile } = useMutationWithDispatch(
    getUserProfile
  );

  useEffect(() => {
    setPageConfig({ title: PAGE_CONFIG.TITLE });
  }, [setPageConfig]);

  const onSubmit = useCallback(
    async (data: AmbassadorSignupFormData) => {
      try {
        const payload = getAmbassadorSignupPayload(data);
        const res = await submitSignup(payload);

        if (res?.success && res?.data) {
          await handleAmbassadorSignupSuccess(
            res.data,
            navigate,
            fetchUserProfile
          );
        }
      } catch (error) {
      }
    },
    [submitSignup, navigate, fetchUserProfile]
  );

  if (searchParams?.get("email")) {
    return <ExistingAmbassadorSignup />;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack>
        <FormTextField
          name="email"
          control={control}
          label="Email"
          required
          error={errors.email?.message}
          textFieldProps={{ type: "email" }}
        />

        <FormTextField
          name="firstName"
          control={control}
          label="First Name"
          required
          error={errors.firstName?.message}
        />

        <FormTextField
          name="lastName"
          control={control}
          label="Last Name"
          required
          error={errors.lastName?.message}
        />

        <FormTextField
          name="password"
          control={control}
          label="Password"
          required
          error={errors.password?.message}
          textFieldProps={{ type: "password" }}
        />

        <FormTextField
          name="confirmPassword"
          control={control}
          label="Repeat Password"
          required
          error={errors.confirmPassword?.message}
          textFieldProps={{ type: "password" }}
        />

        <Stack gap={2} my={2}>
          <PrimaryButton loading={submitting}>Create an account</PrimaryButton>
          <Typography variant="medium" sx={{ textAlign: "center" }}>
            Have an account?
            <Link
              to={ROUTE_PATHS.AUTH.LOGIN}
              style={{ textDecoration: "none" }}
            >
              <Typography
                component="span"
                sx={{ ml: 1, cursor: "pointer", display: "inline" }}
                variant="body-ibmPlexMono-medium"
                color={APP_COLORS.secondaryDark}
              >
                Sign In
              </Typography>
            </Link>
          </Typography>
        </Stack>
      </Stack>
    </form>
  );
}

export default AmbassadorSignup;
